import { useMemo, useState } from 'react';

import {
  CAMPAIGN_TIMEZONE,
  SCHEDULE_MOMENTS,
  scheduleProblems,
  ScheduleMoment,
} from 'app/utils/campaigns/schedule';
import {
  advancedError,
  AdvancedFormState,
  emptyAdvanced,
  toAdvancedRequest,
} from 'app/pages/RewardAndRecognition/Campaigns/AdvancedFields';
import {
  emptyRecurrence,
  RecurrenceFormState,
  recurrenceError,
  toRecurrenceRule,
} from 'app/utils/campaigns/recurrence';

import {
  CampaignScope,
  CreateCampaignRequest,
  ForecastScheduleRequest,
  GroupSelection,
  NominatorEligibility,
  NomineeEligibility,
  WinnerEligibility,
} from '@aslanpay/portal-common-lib';

export interface CreateCampaignFormState {
  name: string;
  description: string;
  scope: CampaignScope;
  /** The one pride a GROUP-scoped campaign belongs to. */
  groupId: string;
  groupSelection: GroupSelection;
  groupIds: string[];
  nominatorEligibility: NominatorEligibility;
  /** The prides whose members may nominate, read only under SELECTED_GROUPS. */
  voterGroupIds: string[];
  nomineeEligibility: NomineeEligibility;
  /** The shortlist anybody may be picked from, read only under SELECTED. */
  nomineeIndividualIds: string[];
  nominationsPerPerson: string;
  valuesRequired: boolean;
  allowWinnerWithoutNominations: boolean;
  /** Who may win where a winner may be picked without nominations. Kept while that is off, so turning it
   * back on offers the same choice again rather than starting over. */
  winnerEligibility: WinnerEligibility;
  awardPoints: string;
  nominationsOpenAt: string;
  nominationsCloseAt: string;
  reviewOpensAt: string;
  reviewClosesAt: string;
  announcementAt: string;
  timezone: string;
  recurrence: RecurrenceFormState;
  advanced: AdvancedFormState;
}

/** The field an error is shown under. The four schedule inputs answer to one, since the rules span them. */
export type CreateCampaignField =
  | 'name'
  | 'description'
  | 'groupIds'
  | 'nominatorEligibility'
  | 'voterGroupIds'
  | 'nomineeEligibility'
  | 'nomineeIndividualIds'
  | 'winnerEligibility'
  | 'nominationsPerPerson'
  | 'awardPoints'
  | ScheduleMoment
  | 'recurrence'
  | 'advanced';

/**
 * A problem with one field. A blank the campaign still needs is {@code missing} and is held back until
 * the form is submitted, so filling it in from the top does not turn the rest red on the way down.
 * Anything else contradicts what has already been entered and is said as soon as the field is touched.
 */
interface FieldError {
  message: string;
  missing?: boolean;
}

export type CreateCampaignErrors = Partial<Record<CreateCampaignField, FieldError>>;

export const emptyForm: CreateCampaignFormState = {
  name: '',
  description: '',
  scope: 'COMPANY',
  groupId: '',
  groupSelection: 'ALL',
  groupIds: [],
  nominatorEligibility: 'ALL_COMPANY',
  voterGroupIds: [],
  nomineeEligibility: 'ALL_COMPANY',
  nomineeIndividualIds: [],
  nominationsPerPerson: '1',
  valuesRequired: true,
  allowWinnerWithoutNominations: false,
  winnerEligibility: 'ALL_COMPANY',
  awardPoints: '',
  nominationsOpenAt: '',
  nominationsCloseAt: '',
  reviewOpensAt: '',
  reviewClosesAt: '',
  announcementAt: '',
  timezone: CAMPAIGN_TIMEZONE,
  recurrence: emptyRecurrence,
  advanced: emptyAdvanced,
};

/** Matches the campaign lifecycle job's cron. A time between two runs is only acted on at the next one. */
export const SCHEDULE_STEP_MINUTES = 30;

/** A half-filled entry carries a date or a time but not both, which does not parse and is not yet set. */
const isSet = (value: string) => !!value && !Number.isNaN(new Date(value).getTime());

const toIsoOrEmpty = (value: string) => (isSet(value) ? new Date(value).toISOString() : '');

const fieldFor = (key: keyof CreateCampaignFormState): CreateCampaignField => {
  // Choosing to fan out to named prides is what asks for a pride, so the requirement shows with the
  // picker rather than waiting for a first tick that may never come.
  if (key === 'groupSelection') return 'groupIds';

  return key as CreateCampaignField;
};

export function useCreateCampaignForm(companyId: string) {
  const [form, setForm] = useState<CreateCampaignFormState>(emptyForm);
  const [touched, setTouched] = useState<CreateCampaignField[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const setField = <K extends keyof CreateCampaignFormState>(
    key: K,
    value: CreateCampaignFormState[K]
  ) => {
    const field = fieldFor(key);
    setTouched((current) => (current.includes(field) ? current : [...current, field]));
    setForm((current) => ({ ...current, [key]: value }));
  };

  // ALL_GROUP eligibility resolves against the campaign's own pride. A GROUP-scoped campaign is one pride,
  // and a company one that fans out is a pride per child — a single company-wide winner is neither.
  const fansOut = form.scope === 'GROUP' || form.groupSelection !== 'NONE';

  const errors = useMemo<CreateCampaignErrors>(() => {
    const found: CreateCampaignErrors = {};

    if (!form.name.trim()) found.name = { message: 'Give the campaign a name.', missing: true };

    if (!form.description.trim()) {
      found.description = {
        message: 'Describe the campaign, so people know what it is for.',
        missing: true,
      };
    }

    // ALL_GROUP resolves against the campaign's own pride, and a campaign with one company-wide
    // winner has none, so the backend rejects the pair.
    if (!fansOut && form.nominatorEligibility === 'ALL_GROUP') {
      found.nominatorEligibility = {
        message: 'A company-wide winner has no pride, so nominators cannot be limited to one.',
      };
    }
    if (!fansOut && form.nomineeEligibility === 'ALL_GROUP') {
      found.nomineeEligibility = {
        message: 'A company-wide winner has no pride, so nominees cannot be limited to one.',
      };
    }
    if (!fansOut && form.allowWinnerWithoutNominations && form.winnerEligibility === 'ALL_GROUP') {
      found.winnerEligibility = {
        message: 'A company-wide winner has no pride, so the winner cannot be limited to one.',
      };
    }
    if (form.scope === 'GROUP' && !form.groupId) {
      found.groupIds = { message: 'Choose the pride this award belongs to.', missing: true };
    }
    if (form.nominatorEligibility === 'SELECTED_GROUPS' && form.voterGroupIds.length === 0) {
      found.voterGroupIds = {
        message: 'Choose at least one pride whose people may nominate.',
        missing: true,
      };
    }
    if (form.nomineeEligibility === 'SELECTED' && form.nomineeIndividualIds.length === 0) {
      found.nomineeIndividualIds = {
        message: 'Choose at least one person who may be nominated.',
        missing: true,
      };
    }
    if (
      form.scope === 'COMPANY' &&
      form.groupSelection === 'SELECTED' &&
      form.groupIds.length === 0
    ) {
      found.groupIds = {
        message: 'Choose at least one pride to run the campaign in.',
        missing: true,
      };
    }

    const perPerson = Number(form.nominationsPerPerson);
    if (!Number.isInteger(perPerson) || perPerson < 1) {
      found.nominationsPerPerson = {
        message: 'Nominations per person must be a whole number of at least one.',
        missing: form.nominationsPerPerson === '',
      };
    }

    if (form.awardPoints !== '') {
      const award = Number(form.awardPoints);
      if (!Number.isInteger(award) || award < 0) {
        found.awardPoints = {
          message: 'The award must be a whole number of points, or left empty for no award.',
        };
      }
    }

    const advancedProblem = advancedError(form.advanced);
    if (advancedProblem) {
      found.advanced = { message: advancedProblem };
    }

    const recurrenceProblem = recurrenceError(form.recurrence);
    if (recurrenceProblem) {
      found.recurrence = { message: recurrenceProblem };
    }

    // Each moment answers for itself, so a reader is told which step to change rather than that the schedule
    // as a whole is wrong.
    Object.entries(
      scheduleProblems(
        {
          nominationsOpenAt: form.nominationsOpenAt,
          nominationsCloseAt: form.nominationsCloseAt,
          reviewOpensAt: form.reviewOpensAt,
          reviewClosesAt: form.reviewClosesAt,
          announcementAt: form.announcementAt,
        },
        SCHEDULE_STEP_MINUTES
      )
    ).forEach(([moment, message]) => {
      found[moment as ScheduleMoment] = {
        message,
        missing: message === 'Set a date and a time.',
      };
    });

    return found;
  }, [form, fansOut]);

  const request = useMemo<CreateCampaignRequest>(
    () => ({
      companyId,
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      campaignType: 'VOTING',
      scope: form.scope,
      groupId: form.scope === 'GROUP' ? form.groupId : undefined,
      groupSelection: form.scope === 'GROUP' ? undefined : form.groupSelection,
      groupIds:
        form.scope === 'COMPANY' && form.groupSelection === 'SELECTED' ? form.groupIds : undefined,
      nominatorEligibility: form.nominatorEligibility,
      voterGroupIds:
        form.nominatorEligibility === 'SELECTED_GROUPS' ? form.voterGroupIds : undefined,
      nomineeEligibility: form.nomineeEligibility,
      nomineeIndividualIds:
        form.nomineeEligibility === 'SELECTED' ? form.nomineeIndividualIds : undefined,
      nominationsPerPerson: Number(form.nominationsPerPerson),
      valuesRequired: form.valuesRequired,
      allowWinnerWithoutNominations: form.allowWinnerWithoutNominations,
      winnerEligibility: form.allowWinnerWithoutNominations ? form.winnerEligibility : undefined,
      awardPoints: form.awardPoints === '' ? undefined : Number(form.awardPoints),
      nominationsOpenAt: toIsoOrEmpty(form.nominationsOpenAt),
      nominationsCloseAt: toIsoOrEmpty(form.nominationsCloseAt),
      reviewOpensAt: toIsoOrEmpty(form.reviewOpensAt),
      reviewClosesAt: toIsoOrEmpty(form.reviewClosesAt) || undefined,
      announcementAt: toIsoOrEmpty(form.announcementAt),
      timezone: form.timezone || undefined,
      ...toAdvancedRequest(form.advanced),
      recurring: form.recurrence.recurring,
      recurrenceRule: toRecurrenceRule(form.recurrence),
    }),
    [companyId, form]
  );

  /**
   * What the forecast is asked for, or nothing where there is no repeat to forecast. Absent while the
   * schedule is incomplete or the recurrence is unsound, so the service is never asked a question that
   * cannot be answered.
   */
  const forecastRequest = useMemo<ForecastScheduleRequest | undefined>(() => {
    const scheduleIsSound = SCHEDULE_MOMENTS.every(({ key }) => !errors[key]);

    if (!form.recurrence.recurring || !scheduleIsSound || errors.recurrence) return undefined;

    const rule = toRecurrenceRule(form.recurrence);
    if (!rule) return undefined;

    return {
      companyId,
      nominationsOpenAt: toIsoOrEmpty(form.nominationsOpenAt),
      nominationsCloseAt: toIsoOrEmpty(form.nominationsCloseAt),
      reviewOpensAt: toIsoOrEmpty(form.reviewOpensAt),
      reviewClosesAt: toIsoOrEmpty(form.reviewClosesAt) || undefined,
      announcementAt: toIsoOrEmpty(form.announcementAt),
      timezone: form.timezone,
      recurrenceRule: rule,
      limit: 13,
    };
  }, [companyId, form, errors]);

  const reset = () => {
    setForm(emptyForm);
    setTouched([]);
    setIsSubmitted(false);
  };

  const errorFor = (field: CreateCampaignField) => {
    const error = errors[field];
    if (!error) return undefined;
    if (error.missing) return isSubmitted ? error.message : undefined;

    return touched.includes(field) || isSubmitted ? error.message : undefined;
  };

  const isValid = Object.keys(errors).length === 0;

  /** Records the attempt so every outstanding blank is named at once, and says whether it can go ahead. */
  const submit = () => {
    setIsSubmitted(true);
    return isValid;
  };

  return { form, setField, reset, fansOut, errorFor, isValid, submit, request, forecastRequest };
}
